import { type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * Abracadabra's shots (the Magician set's gun, ADR-0160 amended): a playing card spinning end over end (white, a red
 * heart; its red back as it turns), a few sparkles trailing it; a puff of white smoke and stars at the muzzle; a puff of
 * smoke and sparkles where it lands. And the flourish's white dove flying (`magician_dove`) and a card tumbling
 * (`magician_card`).
 */

/** A four-pointed sparkle at (cx, cy) of arm `r`: its heart white, its arms gold. */
const sparkle = (x: number, y: number, cx: number, cy: number, r: number): Tone => {
  const [dx, dy] = [Math.abs(x - cx), Math.abs(y - cy)];
  if (dx < 0.6 && dy < 0.6) return 'w';
  if ((dx < 0.6 && dy < r) || (dy < 0.6 && dx < r)) return dx + dy < r * 0.5 ? 'w' : 'y';
  return null;
};

/** The heart on a card's face (5 × 4). */
const HEART = ['.r.r.', 'rrrrr', '.rrr.', '..r..'];

/**
 * A card at (cx, cy), 7 × 10 face on, turned end over end: `turn` 0 face on, 1 three-quarters, 2 edge on, 3 its back
 * three-quarters. Face: white, a grey shade down its far edge, a red heart; back: red, a white border.
 */
const card = (x: number, y: number, cx: number, cy: number, turn: number): Tone => {
  const half = ([5, 3.2, 0.7, 3.2] as const)[turn % 4] as number;
  const [dx, dy] = [x - cx, y - cy];
  if (Math.abs(dx) > 3.5 || Math.abs(dy) > half) return null;
  if (turn % 4 === 2) return '4';
  const edge = Math.abs(dx) > 2.6 || Math.abs(dy) > half - 0.9;
  if (turn % 4 === 3) return edge ? 'w' : (Math.floor(dx) + Math.floor(dy)) % 2 ? 'R' : 'r';
  if (edge) return dx > 2.6 ? '5' : 'w';
  // The heart, squashed with the card as it turns.
  const row = HEART[Math.floor((dy / half) * 4 + 2)];
  return row?.[Math.floor(dx + 2.5)] === 'r' ? 'r' : 'w';
};

/** A puff of smoke: round lumps of it [u, v, r] round (cx, cy), `k` times their size; lit on its top-left. */
const cloud = (x: number, y: number, cx: number, cy: number, k: number, hollow: number): Tone => {
  const LUMPS = [
    [0, 0, 4.5],
    [-4, -2, 3.4],
    [3.5, -3, 3.6],
    [5, 2, 3.2],
    [-1, 4, 3.4],
    [-5, 2.5, 2.8],
    [1, -5, 2.8],
  ] as const;
  const [dx, dy] = [(x - cx) / k, (y - cy) / k];
  if (Math.hypot(dx, dy) < hollow) return null;
  for (const [u, v, r] of LUMPS) {
    const d = Math.hypot(dx - u, dy - v);
    if (d > r) continue;
    // Each lump lit on its own top-left, shaded under.
    const l = (-(dx - u) - (dy - v)) / r;
    return l > 0.45 ? 'w' : l < -0.75 ? '4' : '5';
  }
  return null;
};

/** The card in flight, its middle at the pellet's head (21, 8), a trail of sparkles behind it. */
const flying = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const k = card(x, y, 21, 8, f);
    if (k) return k;
    for (const [sx, sy, r] of [
      [13 - f, 6 + (f % 2), 2.2],
      [7 - f, 10 - (f % 2), 1.6],
    ] as const) {
      const s = sparkle(x, y, sx, sy, r);
      if (s) return s;
    }
    return null;
  });

/** A flash at the muzzle opening into a white puff of smoke, stars flung out of it, thinning to a ring. */
const puff = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [cx, cy] = [8 + f * 4, 20];
    if (f === 0) return sparkle(x, y, 7, 20, 7) ?? (Math.hypot(x - 7, y - 20) < 3.5 ? 'w' : null);
    for (const [sx, sy, r] of [
      [cx + 10 + f * 2, cy - 7, 3 - f * 0.5],
      [cx + 4, cy + 9 + f, 2.4 - f * 0.4],
    ] as const) {
      const s = sparkle(x, y, sx, sy, r);
      if (s) return s;
    }
    return cloud(x, y, cx, cy, 0.9 + f * 0.25, f === 2 ? 3.2 : 0);
  });

/** Where it lands: a flash, then a puff of white smoke opening into a ring as it thins, stars flung out of it. */
const poof = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot(x - 22, y - 22);
    if (f === 0) return sparkle(x, y, 22, 22, 9) ?? (d < 5 ? 'w' : d < 7 ? '5' : null);
    // Stars flung out of it.
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 - 0.5;
      const r = 9 + f * 4;
      const s = sparkle(x, y, 22 + Math.cos(a) * r, 22 + Math.sin(a) * r, 3.2 - f * 0.6);
      if (s) return s;
    }
    const k = ([0, 1.1, 1.35, 1.5] as const)[f] as number;
    const tone = cloud(x, y, 22, 22, k, ([0, 0, 2.6, 4.2] as const)[f] as number);
    return f === 3 && tone === 'w' ? '5' : tone;
  });

/** A white dove (16 × 12) flying right: its wings up, level, down, level; a gold beak, a dark eye, a fanned tail. */
const DOVE = [
  `
  ................
  ..ww............
  ..www...........
  ...www..........
  ...wwww.........
  ....wwww....ww..
  ....5wwwwwwwwkw.
  w..5wwwwwwwwwwwY
  ww5wwwwwwwwwww..
  .ww55wwwwwww5...
  ..5..555555.....
  ................
  `,
  `
  ................
  ................
  ................
  ................
  ............ww..
  ...55wwwwwwwwkw.
  w.5wwwwwwwwwwwwY
  ww5wwwwwwwwwww..
  .ww5wwwwwwww5...
  ..5.55555555....
  ................
  ................
  `,
  `
  ................
  ................
  ................
  ................
  ............ww..
  ......wwwwwwwkw.
  w....wwwwwwwwwwY
  ww..5wwwwwwwww..
  .ww5wwwwwwww5...
  ..5.5wwww555....
  .....5www.......
  ......55........
  `,
] as const;
const dove = (f: number) => (c: PixelCanvas) => c.stamp(DOVE[([0, 1, 2, 1] as const)[f] as number] as string, 0, 0);

export default [
  sprite({
    name: 'card_shot',
    ...shot,
    notes: `Abracadabra's bullet (ADR-0160 amended): a playing card at the pellet's head spinning end over end over 4
frames (face on: white, a red heart, a grey far edge; three-quarters; edge on; its red-checked back), two sparkles
trailing it. Keeps its own turn (upright). Rubric (4×): S4 R5 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 16, frames: frames4(flying) } },
  }),
  sprite({
    name: 'card_muzzle',
    ...muzzle,
    notes: `Abracadabra's muzzle flash (ADR-0160 amended): a white and gold four-pointed flash opening into a puff of
white smoke (round lumps, each lit top-left), thinning to a ring, two sparkles flung out of it. Points right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(puff) } },
  }),
  sprite({
    name: 'card_burst',
    ...burst,
    notes: `Abracadabra's hit (ADR-0160 amended): a four-pointed white flash, then a lumpy puff of white smoke opening into
a ring as it thins, five gold sparkles flung out round it. 4 frames. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 16, loop: false, frames: frames4(poof) } },
  }),
  sprite({
    name: 'magician_dove',
    group: 'fx',
    size: [16, 12],
    origin: [8, 6],
    density: 4,
    notes: `The Magician's knock-out dove (ADR-0160 amended): a plump white dove flying right, a fanned tail, a gold beak,
its wings up, level, down, level over 4 frames (drawn as grids). Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { fly: { fps: 12, frames: frames4(dove) } },
  }),
  sprite({
    name: 'magician_card',
    group: 'fx',
    size: [12, 12],
    origin: [6, 6],
    density: 4,
    notes: `The Magician's knock-out card (ADR-0160 amended): a playing card tumbling end over end over 4 frames (its face,
a red heart; three-quarters; edge on; its red back). Rubric (4×): S4 R5 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 10, frames: frames4((f) => (c) => paint(c, (x, y): Tone => card(x, y, 6, 6, f))) } },
  }),
];
